import { useMemo } from "preact/hooks";
import fuzzysort from "fuzzysort";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { usePlaces } from "../hooks/useWidgetQueries";
import { useNavigation } from "../lib/router";
import { useUrlStringState, useUrlArrayState } from "../hooks/useUrlState";
import { getLocalizedName } from "../lib/localization";
import { PlaceCard } from "../components/places/PlaceCard";
import { EmptyState } from "../components/ui/EmptyState";
import { FilterDropdown } from "../components/ui/FilterDropdown";
import { SearchInput } from "../components/ui/SearchInput";
import {
  ArtistCardSkeleton,
  FilterDropdownSkeleton,
} from "../components/ui/Skeleton";

type PlacesPageProps = {
  onNavigate: (path: string, elementId?: string) => void;
};

// Synthetic place type id for places without a place_type ("Stages").
// Also accepted in block/shortcode/Wix config, e.g. place_type_id="stages".
export const STAGES_PLACE_TYPE_ID = "stages";

function matchesPlaceTypeIds(
  place: { place_type?: { _id: string } | null },
  ids: string[],
) {
  return place.place_type
    ? ids.includes(place.place_type._id)
    : ids.includes(STAGES_PLACE_TYPE_ID);
}

export function PlacesPage({ onNavigate }: PlacesPageProps) {
  const {
    editionId,
    locale,
    edition,
    lockedPlaceTypeIds,
    placeTypeName: configPlaceTypeName,
  } = useEdition();
  const t = useTranslations();
  const { toPlace } = useNavigation();
  const { data: places, loading } = usePlaces(editionId);

  const [search, setSearch] = useUrlStringState("search", "");
  const [selectedPlaceTypes, setSelectedPlaceTypes] = useUrlArrayState(
    "placeTypes",
    [],
  );

  const sortedPlaces = useMemo(() => {
    if (!places) return [];
    return [...places].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [places]);

  // Extract unique place types
  const placeTypes = useMemo(() => {
    if (!sortedPlaces.length) return [];
    const typeMap = new Map<
      string,
      NonNullable<(typeof sortedPlaces)[number]["place_type"]>
    >();
    for (const place of sortedPlaces) {
      if (place.place_type && !typeMap.has(place.place_type._id)) {
        typeMap.set(place.place_type._id, place.place_type);
      }
    }
    return Array.from(typeMap.values()).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }, [sortedPlaces]);

  // Places without a place_type are exposed as a synthetic "Stages" type.
  const hasUntypedPlaces = useMemo(
    () => sortedPlaces.some((place) => !place.place_type),
    [sortedPlaces],
  );

  const placeTypeOptions = useMemo(() => {
    const options: { id: string; label: string }[] = placeTypes.map((pt) => ({
      id: pt._id,
      label:
        getLocalizedName(pt, locale, edition?.default_locale_code) ?? "",
    }));
    if (hasUntypedPlaces) {
      options.unshift({ id: STAGES_PLACE_TYPE_ID, label: t.stages });
    }
    return options;
  }, [placeTypes, hasUntypedPlaces, locale, edition?.default_locale_code, t]);

  const filteredPlaces = useMemo(() => {
    if (!sortedPlaces.length) return [];

    let filtered = sortedPlaces;

    // Block-configured place type filter takes precedence (OR across ids)
    if (lockedPlaceTypeIds.length > 0) {
      filtered = filtered.filter((place) =>
        matchesPlaceTypeIds(place, lockedPlaceTypeIds),
      );
    } else if (configPlaceTypeName) {
      // Legacy fallback for older blocks/shortcodes using place type name
      filtered = filtered.filter((place) => {
        if (!place.place_type) return false;
        const name = getLocalizedName(
          place.place_type,
          locale,
          edition?.default_locale_code,
        );
        return name === configPlaceTypeName;
      });
    } else if (selectedPlaceTypes.length > 0) {
      // URL place type filter (multi-select by ID)
      filtered = filtered.filter((place) =>
        matchesPlaceTypeIds(place, selectedPlaceTypes),
      );
    }

    // Apply fuzzy search
    if (search) {
      const results = fuzzysort.go(search, filtered, {
        key: (place) =>
          getLocalizedName(place, locale, edition?.default_locale_code) ?? "",
        threshold: -10000,
      });
      return results.map((result) => result.obj);
    }

    return filtered;
  }, [sortedPlaces, lockedPlaceTypeIds, configPlaceTypeName, selectedPlaceTypes, search, locale, edition]);

  const hasActiveFilters =
    (search && search.length > 0) || selectedPlaceTypes.length > 0;

  const clearFilters = () => {
    setSearch(null);
    setSelectedPlaceTypes([]);
  };

  if (loading) {
    return (
      <div className="festapp-artists-page">
        <div className="festapp-artists-filters">
          <SearchInput
            value=""
            onChange={() => {}}
            placeholder={t.searchPlaces}
            disabled
          />
          <div className="festapp-artists-filter-row">
            <FilterDropdownSkeleton />
          </div>
        </div>
        <div className="festapp-artists-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <ArtistCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="festapp-artists-page">
      <div className="festapp-artists-filters">
        <SearchInput
          value={search ?? ""}
          onChange={(v) => setSearch(v || null)}
          placeholder={t.searchPlaces}
        />
        <div className="festapp-artists-filter-row">
          {placeTypeOptions.length > 1 && lockedPlaceTypeIds.length === 0 && !configPlaceTypeName && (
            <FilterDropdown
              label={t.placeTypes}
              options={placeTypeOptions}
              selected={selectedPlaceTypes}
              onChange={setSelectedPlaceTypes}
            />
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="festapp-btn festapp-btn-ghost festapp-btn-sm"
              onClick={clearFilters}
            >
              {t.clearFilters}
            </button>
          )}
        </div>
      </div>

      {filteredPlaces.length === 0 ? (
        <EmptyState
          message={t.noPlacesFound}
          onClearFilters={hasActiveFilters ? clearFilters : undefined}
        />
      ) : (
        <div className="festapp-artists-grid">
          {filteredPlaces.map((place) => (
            <PlaceCard
              key={place._id}
              place={place}
              onClick={() => onNavigate(toPlace(place._id), place._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
